import React, { useState, useRef, useEffect } from 'react';

interface VoiceMessagePlayerProps {
  audioUrl: string;
  isSender?: boolean;
}

export const VoiceMessagePlayer: React.FC<VoiceMessagePlayerProps> = ({ audioUrl, isSender = false }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((err) => {
        console.error('Audio playback failed:', err);
        setHasError(true);
      });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds) || seconds === Infinity) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 240,
      maxWidth: 320,
      padding: '8px 10px',
      borderRadius: 14,
      background: isSender ? 'rgba(255, 255, 255, 0.15)' : '#f8fafc',
      border: isSender ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid #e2e8f0',
      color: isSender ? '#fff' : '#1e293b',
      boxSizing: 'border-box'
    }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => setHasError(true)}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            border: 'none',
            background: isSender ? '#fff' : '#4f46e5',
            color: isSender ? '#4f46e5' : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            fontSize: 14,
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            transition: 'transform 0.1s ease',
          }}
          title={isPlaying ? 'Pause' : 'Play Voice Message'}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>

        {/* Waveform / Progress Slider */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2px', opacity: 0.9 }}>
              🎤 Voice Message
            </span>
            <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.75 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <input
            type="range"
            min={0}
            max={duration || 100}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            style={{
              width: '100%',
              height: 4,
              borderRadius: 2,
              accentColor: isSender ? '#fff' : '#4f46e5',
              cursor: 'pointer',
              opacity: 0.9
            }}
          />
        </div>
      </div>

      {/* Direct link fallback if browser audio codec issue */}
      {hasError && (
        <div style={{ fontSize: 11, color: isSender ? '#fed7aa' : '#b45309', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span>⚠️ Codec notice:</span>
          <a
            href={audioUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: isSender ? '#fff' : '#2563eb', fontWeight: 600, textDecoration: 'underline' }}
          >
            Click to play/download audio
          </a>
        </div>
      )}
    </div>
  );
};

export default VoiceMessagePlayer;
